import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ToastPopup from '../popups/ToastPopup';
import { apiCall } from '../main/ApiCall';
import "../css/others/template.css";
import "../css/others/Table.css";
import { EditEmailTemplate, createToastMessage, ToastMessage } from '../interFaces/interfaces';
const extractPlaceholders = (text: string): string[] => {
  const regex = /{{\s*([^}]+?)\s*}}/g;
  const matches = new Set<string>();
  let match;
  while ((match = regex.exec(text)) !== null) {
    matches.add(`{{${match[1].trim()}}}`);
  }
  return Array.from(matches);
};
const EditTemplate: React.FC = () => {
  const { clientId, templateId } = useParams();
  const navigate = useNavigate();
  const username = 'Admin';
  const [template, setTemplate] = useState<EditEmailTemplate | null>(null);
  const [originalTemplate, setOriginalTemplate] = useState<EditEmailTemplate | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<ToastMessage | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  useEffect(() => {
    const fetchTemplate = async () => {
      try {
        const data = await apiCall('get', `/api/email-templates/client/${clientId}/template/${templateId}`);
        const parsedData = { ...data, body: data.body ? data.body.replace(/\\n/g, '\n') : '' };
        setTemplate(parsedData);
        setOriginalTemplate(parsedData);
      } catch (err) {
        console.error('Failed to load template');
      }
    };
    if (templateId) { fetchTemplate(); }
  }, [clientId, templateId]);
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (!template) return;
    const { name, value } = e.target;
    const updated = { ...template, [name]: name === 'type' ? value.toUpperCase().replace(/\s+/g, '_') : value };
    const subjectPlaceholders = name === 'subject' ? extractPlaceholders(value) : extractPlaceholders(updated.subject);
    const bodyPlaceholders = name === 'body' ? extractPlaceholders(value) : extractPlaceholders(updated.body);
    updated.placeholders = Array.from(new Set([...subjectPlaceholders, ...bodyPlaceholders]));
    setTemplate(updated);
  };
  const validateForm = (): boolean => {
    if (!template) return false;
    if (!template.name.trim()) {
      setMessage(createToastMessage('Please fill in the Template Name field', "warning", 3000));
      return false;
    }
    if (!template.subject.trim()) {
      setMessage(createToastMessage('Please fill in the Subject field', "warning", 3000));
      return false;
    }
    if (!template.body.trim()) {
      setMessage(createToastMessage('Please fill in the Body field', "warning", 3000));
      return false;
    }
    return true;
  };
  const handleSubmit = async () => {
    if (!template?.templateId) return;
    if (!validateForm()) return;
    try {
      setIsLoading(true);
      const payload: EditEmailTemplate = { ...template, body: template.body, placeholders: extractPlaceholders(template.subject + '\n' + template.body) };
      await apiCall('put', `/api/email-templates/${template.templateId}?username=${username}&clientId=${clientId}`, payload);
      setMessage(createToastMessage('Template updated successfully!', "success", 3000));
      setTimeout(() => { window.location.reload(); }, 3000);
      setIsEditMode(false);
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message;
      setMessage(createToastMessage(errorMessage, "error", 3000));
    } finally {
      setIsLoading(false);
    }
  };
  const handleCancelEdit = () => {
    setTemplate(originalTemplate);
    setIsEditMode(false);
  };
  if (!template) return null;
  const handleBack = () => { navigate(-1); };
  return (
    <div className="template-page-container">
      <div className="template-page-header">
        <h2>Email Template Details</h2>
      </div>
      <form className="template-page-form">
        <div className="template-page-field">
          <label>Type</label>
          <input name="type" value={template.type} onChange={handleInputChange} disabled={!isEditMode || isLoading} placeholder="e.g., user welcome (will become USER_WELCOME)" />
          <small className="small">Will be automatically converted to UPPER_CASE_WITH_UNDERSCORES</small>
        </div>
        <div className="template-page-field">
          <label>Template Name</label>
          <input name="name" value={template.name} onChange={handleInputChange} disabled={!isEditMode || isLoading} placeholder="e.g., Enter an Email Template Name" />
        </div>
        <div className="template-page-field">
          <label>Subject</label>
          <input name="subject" value={template.subject} onChange={handleInputChange} disabled={!isEditMode || isLoading} placeholder="e.g., Welcome to {{company_name}}!" />
        </div>
        <div className="template-page-field">
          <label>Body</label>
          <textarea name="body" value={template.body} rows={10} onChange={handleInputChange} disabled={!isEditMode || isLoading} placeholder={`Dear {{first_name}},\n\nWelcome to {{company_name}}! We're excited to have you on board.\n\nBest regards,\n{{company_name}} Team`} />
          {template.placeholders.length > 0 && (
            <div className="template-page-placeholders">
              <strong>Placeholders:</strong> {template.placeholders.map(p => `${p}`).join(', ')}
            </div>
          )}
        </div>
      </form>
      <div className="buttons">
        <button type="button" onClick={handleBack} className="cancelbutton" disabled={isLoading}>Back</button>
        {!isEditMode ? (
          <button type="button" className="cancelbutton" onClick={() => setIsEditMode(true)}>Edit</button>
        ) : (
          <>
            <button type="button" className="cancelbutton" onClick={handleCancelEdit} disabled={isLoading}>Cancel</button>
            <button type="button" className="savebutton" onClick={handleSubmit} disabled={isLoading}>Update</button>
          </>
        )}
      </div>
      {message && (<ToastPopup message={message.text} type={message.type} duration={message.duration} onClose={() => setMessage(null)} />)}
    </div>
  );
};
export default EditTemplate;